<script setup lang="ts">
  import { CustomersService } from '@/api/services/customers.service';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { ICustomer } from '@/interfaces/ICustomer';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import ModalsCustomer from './ModalsCustomer.vue';
  import { IDataOptions } from '@/interfaces/IDataOptions';
  import { debounce } from '@/utils';
  import ModalsCloseButton from '../ModalsCloseButton.vue';

  const restaurantStore = useRestaurantStore();
  const ui = useUi();
  const modals = useModal();
  const emit = defineEmits(['close']);

  const paginationData = ref<IDataOptions>({
    page: 1,
    itemsPerPage: 5,
    mustSort: true,
    sortBy: ['name'],
    sortDesc: [false],
    search: '',
  });

  const totalItems = ref(0);
  const customers = ref<ICustomer[] | null>(null);
  const loading = ref(false);
  const willLoadCustomers = ref(false);

  async function loadCustomers() {
    willLoadCustomers.value = false;

    if (loading.value) return;

    if (!paginationData.value.search?.trim()) {
      customers.value = null;
      totalItems.value = 0;
      return;
    }

    try {
      loading.value = true;
      const response = await CustomersService.getAll({
        restaurantId: restaurantStore.restaurant.id,
        options: paginationData.value,
      });
      customers.value = response.items;
      totalItems.value = response.total;
    } catch (error) {
      ui.serverError(error, 'Error al cargar los clientes');
    } finally {
      loading.value = false;
    }
  }

  const debouncedLoadCustomers = debounce(loadCustomers, 500);

  watch(
    () => paginationData.value.page,
    () => {
      loadCustomers();
    },
    { immediate: true, deep: true }
  );

  watch(
    () => paginationData.value.search,
    () => {
      customers.value = null;
      totalItems.value = 0;
      willLoadCustomers.value = true;
      debouncedLoadCustomers();
    },
    { immediate: true, deep: true }
  );

  const filteredCustomers = computed(() => {
    return customers.value;
  });

  const { proxy } = getCurrentInstance();

  function handleChooseCustomer(customer: ICustomer) {
    proxy.$parent.$emit('chooseCustomer', customer);
  }

  function openNewCustomerModal() {
    modals.show(
      ModalsCustomer,
      {},
      {
        name: 'customer',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: false,
      },
      {
        saved: (customer: ICustomer) => {
          handleChooseCustomer(customer);
        },
      }
    );
  }

  function getCustomerDesc(customer: ICustomer) {
    const parts = [`#${customer.nativeId}`];

    if (customer.taxId) {
      parts.push(customer.taxId);
    }

    if (customer.email) {
      parts.push(customer.email);
    }

    if (customer.tel) {
      parts.push(customer.tel);
    }

    if (customer.address) {
      parts.push(customer.address);
    }

    return parts.join(' - ');
  }
</script>

<template>
  <v-card class="modal-customers">
    <ModalsCloseButton @close="emit('close')" />
    <v-card-title>
      <span>Clientes</span>
    </v-card-title>
    <v-card-text class="card-text">
      <div class="modal-body">
        <div class="modal-body-header">
          <div class="search-container">
            <v-text-field
              v-model="paginationData.search"
              placeholder="Buscar por N°, nombre o identificador"
              outlined
              rounded
              dense
              hide-details
              prepend-inner-icon="mdi-magnify"
              class="search-input"
              autofocus="true"
            />
          </div>
          <!-- <v-btn
            color="primary"
            rounded
            depressed
            @click="openNewCustomerModal"
          >
            <v-icon left>mdi-plus</v-icon>
            <span>Nuevo</span>
          </v-btn> -->
        </div>
        <div class="modal-body-content">
          <div v-if="!paginationData.search?.trim()"></div>
          <template v-else-if="customers?.length">
            <v-list>
              <v-list-item
                v-for="customer in filteredCustomers"
                :key="customer.id"
                class="customer-item"
                @click="handleChooseCustomer(customer)"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ customer.name }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ getCustomerDesc(customer) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn
                    color="success"
                    rounded
                    depressed
                    small
                    @click.stop="handleChooseCustomer(customer)"
                  >
                    <v-icon left>mdi-check</v-icon>
                    <span>Elegir</span>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
            <div class="tw-flex tw-justify-center tw-items-center tw-mt-4">
              <v-pagination
                v-model="paginationData.page"
                :length="Math.ceil(totalItems / paginationData.itemsPerPage)"
                :total-visible="5"
              />
            </div>
          </template>
          <div
            v-else-if="loading || willLoadCustomers"
            class="tw-flex tw-justify-center tw-py-4"
          >
            <v-progress-circular indeterminate />
          </div>
          <div
            v-else
            class="tw-flex tw-justify-center tw-items-center tw-py-4 tw-text-gray-500"
          >
            No se encontraron clientes
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<style scoped lang="scss">
  .modal-customers {
    @apply tw-bg-transparent tw-border-none tw-h-full;

    .card-text {
      @apply tw-flex-1 tw-min-h-1 tw-overflow-y-auto;

      .modal-body {
        @apply tw-space-y-2;

        .modal-body-header {
          @apply tw-flex tw-justify-between tw-items-center;

          .search-container {
            @apply tw-flex-1;

            .search-input {
              @apply tw-flex-1;
            }
          }
        }

        .modal-body-content {
          @apply tw-flex-1 tw-min-h-1 tw-overflow-y-auto;

          .customer-item {
            @apply tw-cursor-pointer;
          }
        }
      }
    }
  }
</style>
